import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import {
  Card,
  Nav,
  ProgressBar,
  Spinner,
  Button
} from 'react-bootstrap';

// Local components
import AccountForm from './AccountForm';
import PersonalForm from './PersonalForm';
import BillingForm from './AddressForm';
import Success from './Success';

// Hooks & providers
import { useAppContext } from 'providers/AppProvider';
import useUsers from 'hooks/userManagement/useUsers';
import { useAddUserForm } from 'hooks/userManagement/useAddUserForm';

const navItems = [
  { icon: 'lock', label: 'Account' },
  { icon: 'user', label: 'Personal' },
  { icon: 'globe', label: 'Address' },
  { icon: 'thumbs-up', label: 'Done' }
];

const WizardLayout = ({ userId, isEditMode, initialStaff = null }) => {
  const { isRTL } = useAppContext();
  const { getUserById, patchUserProperties, patchProfileProperties, refetch } = useUsers(0, 20, null, true, { autoFetch: false });

  const [step, setStep] = useState(1);
  const [loadingUser, setLoadingUser] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [errors, setErrors] = useState({});

  // Hook state (initialized by currentUser once fetched)
  const {
    formData,
    handleInputChange,
    handleIdentificationDocChange,
    handleWorkAuthorizationDocChange,
    handleWorkAuthorizationChange,
    handleIdentificationDocTypeChange,
    handleWorkAuthorizationTypeChange,
    submitForm,
    errors: hookErrors
  } = useAddUserForm(currentUser, { initialStaff });

  // Load user when editing
  useEffect(() => {
    console.log('[WizardLayout useEffect] isEditMode:', isEditMode, 'userId:', userId);
    if (!isEditMode || !userId) {
      console.log('[WizardLayout useEffect] Skipping load because not edit mode or no userId');
      return;
    }

    const load = async () => {
      setLoadingUser(true);
      try {
        const user = await getUserById(userId);
        console.log('[WizardLayout] Loaded user:', user);
        if (user) setCurrentUser(user);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load user');
      } finally {
        setLoadingUser(false);
      }
    };

    load();
  }, [isEditMode, userId, getUserById]);

  // Basic validation per step
  const validateStep = () => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.firstName?.trim())
        newErrors.firstName = 'First Name is required';
      if (!formData.email?.trim())
        newErrors.email = 'Email is required';
      if (!isEditMode && !formData.password?.trim())
        newErrors.password = 'Password is required for new users';
    }

    if (step === 2) {
      if (!formData.lastName?.trim())
        newErrors.lastName = 'Last Name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      setErrors({});
      setStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    setErrors({});
    setStep(prev => prev - 1);
  };

  const handleReset = () => {
    setStep(1);
    setErrors({});
    setCurrentUser(null);
  };

  const handleSubmit = async () => {
    if (validateStep()) {
      const isCreating = !isEditMode;
      const toastId = toast.loading(isEditMode ? 'Saving changes…' : 'Creating user…', {
        theme: 'colored',
        className: isCreating ? 'toast-green' : undefined,
      });
      try {
        const result = await submitForm();
        if (result?.success) {
          setStep(4);
        } else {
          const formMsg = result?.errors?.form;
          if (formMsg) {
            toast.error(formMsg);
            return;
          }

          const errorEntries = Object.entries(result?.errors || {});
          if (errorEntries.length) {
            const message = errorEntries
              .map(([field, msg]) => `${field}: ${msg}`)
              .join(' | ');
            toast.error(message);
          } else {
            toast.error('Unable to complete. Please review the form and try again.');
          }
        }
      } catch (err) {
        console.error(err);
        const status = err?.response?.status;
        if (status >= 500) {
          toast.info('Server error occurred. Please try again later.', { theme: 'colored' });
        } else {
          toast.error(err?.message || 'Failed to save user');
        }
      } finally {
        toast.dismiss(toastId);
      }
    }
  };

  return (
    <Card className="theme-wizard mb-5 custom-shadow position-relative">
      {/* Loading Overlay */}
      {loadingUser && (
        <div
          className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center"
          style={{ backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 10 }}
        >
          <Spinner animation="border" variant="primary" />
        </div>
      )}

      {/* Header Navigation */}
      <Card.Header className="bg-body-tertiary px-4 py-2" style={{ paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
        <Nav className="justify-content-center" variant="pills">
          {navItems.map((item, idx) => (
            <Nav.Item key={item.label}>
              <Nav.Link
                className={classNames('fw-semibold', {
                  done: step > idx + 1,
                  active: step === idx + 1
                })}
                onClick={() => setStep(idx + 1)}
                disabled={loadingUser}
                style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem' }}
              >
                <FontAwesomeIcon icon={item.icon} style={{ fontSize: '0.875rem' }} />
                <span className="d-none d-md-inline ms-2">{item.label}</span>
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
      </Card.Header>

      {/* Progress Bar */}
      <ProgressBar now={(step / navItems.length) * 100} style={{ height: 2 }} />

      {/* Step Content */}
      <Card.Body className="fw-normal px-md-6 py-4">
        {step === 1 && (
          <AccountForm
            formData={formData}
            errors={errors}
            handleInputChange={handleInputChange}
            isEditMode={isEditMode}
            userId={userId}
            patchUserProperties={patchUserProperties}
            patchProfileProperties={patchProfileProperties}
            refetch={refetch}
          />
        )}
        {step === 2 && (
          <PersonalForm
            formData={formData}
            errors={errors}
            handleInputChange={handleInputChange}
            userId={currentUser?.id}
            patchUserProperties={patchUserProperties}
            patchProfileProperties={patchProfileProperties}
            refetch={refetch}
            isEditMode={isEditMode}
            onIdentityFilesSelected={handleIdentificationDocChange}
            onWorkAuthFilesSelected={handleWorkAuthorizationDocChange}
            onIdentityTypeChange={handleIdentificationDocTypeChange}
            onWorkAuthTypeChange={handleWorkAuthorizationTypeChange}
            onWorkAuthorizationChange={handleWorkAuthorizationChange}
          />
        )}
        {step === 3 && (
          <BillingForm
            formData={formData}
            errors={errors}
            handleInputChange={handleInputChange}
            isEditMode={isEditMode}
            userId={userId}
            patchProfileProperties={patchProfileProperties}
            refetch={refetch}
          />
        )}
        {step === 4 && <Success reset={handleReset} />}
      </Card.Body>

      {/* Footer Controls */}
      <Card.Footer className={classNames('px-md-6 bg-body-tertiary', { 'd-flex': step < 4, 'd-none': step === 4 })}>
        {step > 1 && (
          <Button variant="link" onClick={handlePrev} disabled={loadingUser}>
            Previous
          </Button>
        )}
        {step < 4 && (
          <Button variant="primary" className="ms-auto" onClick={step === 3 ? handleSubmit : handleNext} disabled={loadingUser}>
            {step === 3 ? (isEditMode ? 'Save Changes' : 'Create User') : 'Next'}
          </Button>
        )}
      </Card.Footer>
    </Card>
  );
};

WizardLayout.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isEditMode: PropTypes.bool,
  initialStaff: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([null])])
};

export default WizardLayout;
